import { cloneDeep, get, zipWith } from 'lodash';
import { createSelector } from 'reselect';

import {
  getModalSplitByOwnership,
  getModalSplitByType,
  getModalSplitPublicTransitLever,
  getModalSplitSharedMobilityLever,
  getNumProjectionYears
} from '../index';

import {
  IAutorickshawSplit,
  ICarSplit,
  IModalSplit,
  IOwnershipMode,
  IPublicTransitSplit,
  ITwoWheelSplit,
  VehicleType
} from './../../types/city';

import {
  MODAL_SPLIT_BY_OWNERSHIP_BAU_GROWTH,
  MODAL_SPLIT_BY_TYPE_BAU_GROWTH,
} from '../../../data/modelConstants';

import {
  adjustTypeGrowthForPublicTransit,
  adjustTypeGrowthForSharedMobility
} from './leverAdjustByType';


import {
  adjustOwnershipGrowthForCar,
  adjustOwnershipGrowthForTwoWheel,
} from './leverAdjustByOwnership';

export const simpleGrowthSeries = (base: number,
                                  growthRate: number,
                                  numCycles: number): number[] => {
  const results = [base];
  for (let i = 0; i < numCycles; i++) {
    results.push(base + growthRate * (i + 1));
  }
  return results;
}

// Given initial split, get the endgame
const projectModalSplit = (modalSplit: IModalSplit, growth: IModalSplit, numYears: number, modes: VehicleType[]): IModalSplit[] => {
  const modeSeries = modes.map(mode => simpleGrowthSeries(modalSplit[mode], growth[mode], numYears));
  // Get a series for each mode, then zip it up
  return zipWith(...modeSeries, (car, twoWheel, threeWheel, publicTransit) => ({
    [VehicleType.car]: car,
    [VehicleType.twoWheel]: twoWheel,
    [VehicleType.threeWheel]: threeWheel,
    [VehicleType.publicTransit]: publicTransit,
  }))
}


// Not DRY, but this works for first pass
export const getProjectedModalSplitByTypeSeries = createSelector(
  [getModalSplitByType, getNumProjectionYears,
  getModalSplitSharedMobilityLever, getModalSplitPublicTransitLever
  ],
  (modalSplit, numYears, sharedMobilityLever, publicTransitLever): IModalSplit[] => {
    // normalize each one
    const modes = [
      VehicleType.car,
      VehicleType.twoWheel,
      VehicleType.threeWheel,
      VehicleType.publicTransit,
    ]

    // Now we need to apply the special cases... ideal situation would be to do a reduce of some sort...
    const growthSharedMobility = adjustTypeGrowthForSharedMobility(modalSplit, MODAL_SPLIT_BY_TYPE_BAU_GROWTH, numYears, sharedMobilityLever);
    const splitSharedMobility = projectModalSplit(modalSplit, growthSharedMobility, numYears, modes).slice(-1)[0];
    const growthPublicTransit = adjustTypeGrowthForPublicTransit(splitSharedMobility, growthSharedMobility,
      numYears, publicTransitLever, modalSplit);

    // Finally, calculate the growth rates after applying each adjustment
    return projectModalSplit(modalSplit, growthPublicTransit, numYears, modes);
  }
)

// paths = list of lists
// Return the list of values for a given piece of the Owner Subtree
// Todo: refactor to pass in simpler object chunks
const getModalOwnerChunks = (paths: string[][], modalSplit: IOwnershipMode, numYears: number, growth: IOwnershipMode) => {
  return paths.map((path: string[]) =>  simpleGrowthSeries(get(modalSplit, path),
                                                          get(growth, path),
                                                          numYears)
  );
}

const inverseSum = (items: number[]) => {
  return 1 / items.reduce((accum, val) => accum + val, 0);
}

export const getProjectedModalSplitByOwnershipSeries = createSelector(
  [getModalSplitByOwnership, getNumProjectionYears, getModalSplitSharedMobilityLever],
  (modalSplit, numYears, sharedMobilityLever): IOwnershipMode[] => {

    // Initially this won't be DRY at all... refactor this another time since each item had diff keys
    // Tuples will be "keyname" and "pathname"
    const carPaths = [
      [VehicleType.car, 'commercial'],
      [VehicleType.car, 'private'],
      [VehicleType.car, 'public'],
    ];
    const growthMap = cloneDeep(MODAL_SPLIT_BY_OWNERSHIP_BAU_GROWTH);

    // FIRST: CARS
    // First, let's calculate New Growth Rate for commercial cars
    const carGrowthMap = adjustOwnershipGrowthForCar(modalSplit[VehicleType.car], numYears, sharedMobilityLever)
    // This mutates the datastructure in-place for simplicity
    growthMap[VehicleType.car] = carGrowthMap;

    const carChunks = getModalOwnerChunks(carPaths, modalSplit, numYears, growthMap);
    const carSeries: ICarSplit[] = zipWith(...carChunks, (commercial, pri, pub) => {
      const subtotalInverse = inverseSum([commercial, pri, pub]);
      return {
        'commercial': commercial * subtotalInverse,
        'private': pri * subtotalInverse,
        'public': pub * subtotalInverse,
      }
    });

    // Checkpoint- car prediction seems to be working
    // console.log('CarGrowthMap', growthMap[VehicleType.car]);

    const rickshawPaths = [
      ['autoRickshaw', 'commercial'],
    ]
    const rickshawChunks = getModalOwnerChunks(rickshawPaths, modalSplit, numYears, growthMap);
    const rickshawSeries: IAutorickshawSplit[] = zipWith(...rickshawChunks, (commercial) => ({
      'commercial': commercial,
    }));

    const twoWheelPaths = [
      [VehicleType.twoWheel, 'commercial'],
      [VehicleType.twoWheel, 'private'],
    ]

    // Adjust the growthmap
    const twoWheelGrowthMap = adjustOwnershipGrowthForTwoWheel(modalSplit[VehicleType.twoWheel], numYears, sharedMobilityLever)
    // Warning: this mutates the datastructure in-place for simplicity
    growthMap[VehicleType.twoWheel] = twoWheelGrowthMap;

    // console.log('TwoWheel Map', twoWheelGrowthMap);

    const twoWheelChunks = getModalOwnerChunks(twoWheelPaths, modalSplit, numYears, growthMap);
    const twoWheelSeries: ITwoWheelSplit[] = zipWith(...twoWheelChunks, (commercial, pri) => {
      const subtotalInverse = inverseSum([commercial, pri]);
      return {
        'commercial': commercial * subtotalInverse,
        'private': pri * subtotalInverse,
      }
    });

    const publicTransitPaths = [
      [VehicleType.publicTransit, 'bus'],
      [VehicleType.publicTransit, 'metro'],
    ]
    const publicTransitChunks = getModalOwnerChunks(publicTransitPaths, modalSplit, numYears, growthMap);
    const publicTransitSeries: IPublicTransitSplit[] = zipWith(...publicTransitChunks, (bus, metro) => {
      const subtotalInverse = inverseSum([bus, metro]);
      return {
        'bus': bus * subtotalInverse,
        'metro': metro * subtotalInverse,
      }
    });

    const combined: IOwnershipMode[] = zipWith(
      carSeries,
      rickshawSeries,
      twoWheelSeries,
      publicTransitSeries,
      (car: ICarSplit, autoRickshaw: IAutorickshawSplit,
      twoWheel: ITwoWheelSplit, publicTransit: IPublicTransitSplit ) => ({
        autoRickshaw,
        car,
        publicTransit,
        twoWheel,
      }
    ));

    // console.log(combined.slice(-1)[0]);
    return combined;
  }
)



// Make sure all subtotals groups add up to 1
export const getProjectedModalSplitByOwnershipSeriesNormalized = createSelector(
  [getProjectedModalSplitByOwnershipSeries],
  (splits): IOwnershipMode[] => {

    return splits.map(split => {

      const carTotalInverse = inverseSum([split.car.commercial, split.car.private, split.car.public]);
      const publicTransitTotalInverse = inverseSum([split.publicTransit.bus, split.publicTransit.metro]);
      const twoWheelTotalInverse = inverseSum([split.twoWheel.commercial, split.twoWheel.private]);

      return {
        autoRickshaw: split.autoRickshaw, // no division needed since it stayed constant
        car: {
          commercial: split.car.commercial * carTotalInverse,
          private: split.car.private * carTotalInverse,
          public: split.car.public * carTotalInverse,
        },
        publicTransit: {
          bus: split.publicTransit.bus * publicTransitTotalInverse,
          metro: split.publicTransit.metro * publicTransitTotalInverse,
        },
        twoWheel: {
          commercial: split.twoWheel.commercial * twoWheelTotalInverse,
          private: split.twoWheel.private * twoWheelTotalInverse,
        }
      }
    })
  }
)


// Create a derived series ensuring all the subgroups add up to some healthy amount

export const makeGetProjectedModalSplitByOwnerAndTypeSeries = () => {
  return createSelector(
    [getProjectedModalSplitByOwnershipSeriesNormalized, getProjectedModalSplitByTypeSeries],
    (splitByOwner, splitByType) => {

      return zipWith(splitByOwner, splitByType, (ownerSplit, typeSplit) => ({
        autoRickshaw: {
          commercial: ownerSplit.autoRickshaw.commercial * typeSplit.threeWheel,
        },
        car: {
          commercial: ownerSplit.car.commercial * typeSplit.car,
          private: ownerSplit.car.private * typeSplit.car,
          public: ownerSplit.car.public * typeSplit.car,
        },
        publicTransit: {
          bus: ownerSplit.publicTransit.bus * typeSplit.publicTransit,
          metro: ownerSplit.publicTransit.metro * typeSplit.publicTransit,
        },
        twoWheel: {
          commercial: ownerSplit.twoWheel.commercial * typeSplit.twoWheel,
          private: ownerSplit.twoWheel.private * typeSplit.twoWheel,
        },
      }));
    }
  );
}


export const getProjectedModalSplitByOwnerAndTypeSeries = makeGetProjectedModalSplitByOwnerAndTypeSeries();



// WEBPACK FOOTER //
// ./src/store/selectors/modalSplit/index.ts